import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
          <span className="text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
            itaobuyspread<span className="text-primary-500">.cam</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Home
          </Link>

          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Categorie
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50/98 backdrop-blur-xl rounded-xl border border-background-200/60 py-2 w-[560px] shadow-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 px-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameEn}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-primary-50 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap group"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-200 transition-colors">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/guida-shopping"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Guida Shopping
          </Link>

          <Link
            to="/articolo/guida-completa-itaobuy-spreadsheet"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Guida 2026
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            itaobuy sheet
          </a>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-6 py-6">
            <div className="flex items-center gap-2 mb-6">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line"></i>
                </span>
                Home
              </Link>
              <Link
                to="/guida-shopping"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-foreground-900 text-background-50 font-semibold text-sm hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
                Guida Shopping
              </Link>
            </div>

            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3 px-1">Categorie Prodotti</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.nameEn}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-primary-50 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap group"
                  >
                    <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-5 space-y-1">
              <Link
                to="/articolo/guida-completa-itaobuy-spreadsheet"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-guide-line text-sm"></i>
                </span>
                Guida Completa 2026
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-external-link-line text-sm"></i>
                </span>
                itaobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-100 text-foreground-600">
                  <i className="ri-shopping-bag-line text-sm"></i>
                </span>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}